import { useEffect, useState } from 'react';
import TaskForm from './TaskForm';
import styles from './astyles/TaskList.module.css';
import { useParams } from 'react-router-dom';
import { useCallback } from 'react';
import { exportToCSV, exportToPDF, formatDataForExport } from '../../utils/exportUtils';
import { Download, FileText } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';

interface Task {
  id: number;
  title: string;
  description: string;
  location: string;
  required_skills: string[];
  due_date?: string;
  created_at: string;
  project_id: number;
}

export default function TaskList() {
  const { projectId } = useParams(); // Get project ID from URL
  const [tasks, setTasks] = useState<Task[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  
const fetchTasks = useCallback(async () => {
  setLoading(true);
  try {
    if (!projectId) {
      throw new Error('Project ID is required to fetch tasks');
    }

    const endpoint = `${API_BASE_URL}/api/projects/${projectId}/tasks`;
    console.log('Fetching tasks from:', endpoint); // Debugging line
    
    const response = await fetch(endpoint);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || 
        errorData.error || 
        `Failed to fetch tasks: ${response.statusText}`
      );
    }
    
    const data = await response.json();
    setTasks(data);
    setError(null);
  } catch (err) {
    console.error('Error fetching tasks:', err);
    setError(err instanceof Error ? err.message : 'Failed to load tasks');
    setTasks([]); // Clear tasks on error
  } finally {
    setLoading(false);
  }
}, [projectId]);
  

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleTaskCreated = () => {
    setShowForm(false);
    setEditingTask(null);
    fetchTasks();
  };

  const handleEdit = (task: Task) => {
    setEditingTask(task);
    setShowForm(true);
  };

  const handleDelete = async (taskId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete: ${response.statusText}`);
      }

      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting task:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete task');
    }
  };

  const cancelDelete = () => setDeleteConfirm(null);

  // Export functions
  const handleExportCSV = () => {
    const formattedData = formatDataForExport(tasks, 'tasks');
    exportToCSV(formattedData, `tasks_${new Date().toISOString().split('T')[0]}`);
  };

  const handleExportPDF = async () => {
    setExportLoading(true);
    try {
      const formattedData = formatDataForExport(tasks, 'tasks');
      await exportToPDF(formattedData, `tasks_${new Date().toISOString().split('T')[0]}`, 'Tasks List');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className={styles.taskListContainer}>
      <div className={styles.taskListHeader}>
        <h2>{projectId ? 'Project Tasks' : 'All Tasks'}</h2>
        <div className={styles.taskControls}>
          {tasks.length > 0 && (
            <div className={styles.exportButtons}>
              <button
                onClick={handleExportCSV}
                className={`${styles.exportBtn} ${styles.csvBtn}`}
                disabled={tasks.length === 0}
              >
                <Download size={16} />
                Export CSV
              </button>
              <button
                onClick={handleExportPDF}
                className={`${styles.exportBtn} ${styles.pdfBtn}`}
                disabled={tasks.length === 0 || exportLoading}
              >
                <FileText size={16} />
                {exportLoading ? 'Exporting...' : 'Export PDF'}
              </button>
            </div>
          )}
          {projectId && (
            <button 
              className={styles.createButton}
              onClick={() => setShowForm(true)}
            >
              Create New Task
            </button>
          )}
        </div>
      </div>
      
      {showForm ? (
        <TaskForm 
          onSuccess={handleTaskCreated} 
          taskToEdit={editingTask}
          projectId={projectId ? parseInt(projectId) : undefined}
          onCancel={() => {
            setShowForm(false);
            setEditingTask(null);
          }}
        />
      ) : (
        <>
          {loading && <p>Loading tasks...</p>}
          {error && <p className={styles.errorMessage}>{error}</p>}
          
          {!loading && !error && tasks.length === 0 && (
            <p>No tasks available. {projectId && 'Create a new task to get started.'}</p>
          )}
          
          {!loading && !error && tasks.length > 0 && (
            <div className={styles.taskGrid}>
              {tasks.map(task => (
                <div key={task.id} className={styles.taskCard}>
                  <h3>{task.title}</h3>
                  <p>{task.description}</p>
                  <div className={styles.taskMeta}>
                    <p>{task.location || 'No location specified'}</p>
                    <p>{task.required_skills?.join(', ') || 'No skills specified'}</p>
                    {task.due_date && <p>Due: {new Date(task.due_date).toLocaleDateString()}</p>}
                  </div>
                  
                  <div className={styles.taskActions}>
                    <button 
                      className={styles.editButton}
                      onClick={() => handleEdit(task)}
                    >
                      Edit
                    </button>
                    
                    {deleteConfirm === task.id ? (
                      <div className={styles.deleteConfirm}>
                        <span>Confirm delete?</span>
                        <button 
                          onClick={() => handleDelete(task.id)}
                          className={styles.confirmButton}
                        >
                          Yes
                        </button>
                        <button 
                          onClick={cancelDelete}
                          className={styles.cancelButton}
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button 
                        className={styles.deleteButton}
                        onClick={() => setDeleteConfirm(task.id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}